/**
 * Messages browsers and Vite use when a code-split chunk cannot be loaded:
 * Chromium, Firefox and Safari word the failed `import()` differently, and
 * Vite's preload helper reports a missing stylesheet separately. TanStack
 * Router's `isModuleNotFoundError` matches the first three, but only lives in
 * `@tanstack/router-core`, which the app does not depend on at runtime.
 */
const CHUNK_LOAD_ERROR_PREFIXES = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
  "Unable to preload CSS",
];

const MAX_CAUSE_DEPTH = 5;

/**
 * Whether `error`, or an error in its `cause` chain, is a failed chunk load.
 *
 * This happens when a deploy replaces the chunk an open tab still points at,
 * or when the connection drops mid-navigation. TanStack Router's lazy route
 * components already reload the page once on this error, so an error boundary
 * that sees it should offer a refresh rather than a generic failure.
 *
 * @alias stale chunk, failed dynamic import, new deploy, vite:preloadError
 */
export function isChunkLoadError(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH && current; depth++) {
    if (!(current instanceof Error)) return false;
    const { message } = current;
    if (CHUNK_LOAD_ERROR_PREFIXES.some((prefix) => message.startsWith(prefix)))
      return true;
    current = current.cause;
  }
  return false;
}
